import { html, property, state, queryAssignedNodes } from 'lit-element';
import { nothing } from 'lit-html';
import { KatLitElement, register } from '../../shared/base';
import { isMobile } from '../../utils/mobile-helpers';
import baseStyles from '../../shared/base/base.lit.scss';
import { KatBreadcrumbItem } from './breadcrumb-item';
import styles from './breadcrumb.lit.scss';

/**
 * @component {kat-breadcrumb} KatalBreadcrumb A breadcrumb is a series of horizontal links that allow users to understand their current place in the navigation hierarchy and navigate back to higher-level pages.
 * @slot default Should be filled with `kat-breadcrumb-item` elements.
 * @subcomponent ./breadcrumb-item.ts
 * @guideline Breadcrumbs should always appear at the top of a page.
 * @guideline Do Use "kat-breadcrumb-item" children.
 * @guideline Dont It is not recommended to use breadcrumbs in multiple places on a page or in the middle of a page.
 * @example SetViaHtml {"content": "<kat-breadcrumb-item label=\"Parent\" href=\"#\"></kat-breadcrumb-item><kat-breadcrumb-item label=\"Group\" href=\"#\"></kat-breadcrumb-item><kat-breadcrumb-item label=\"Category\" href=\"#\"></kat-breadcrumb-item><kat-breadcrumb-item label=\"Current Category\" href=\"#\"></kat-breadcrumb-item>"}
 * @status Production
 * @theme flo
 * @a11y {keyboard}
 * @a11y {sr}
 * @a11y {contrast}
 */
@register('kat-breadcrumb')
export class KatBreadcrumb extends KatLitElement {
  constructor() {
    super();

    this._listeners = [
      {
        target: window,
        listeners: [['resize', () => this.handleWindowResize()]],
      },
    ];
  }

  /** Aria role filling in for semantic HTML */
  @property()
  role = 'list'; // Typically we prefix native attributes with kat, but not here because we want to apply the native attribute to the root element.

  /** Displays a dedicated home icon link, with the URL provided */
  @property({ attribute: 'home-href' })
  homeHref?: string;

  /** Slotted KatBreadcrumbItems. Does not include the homeHref item. */
  @queryAssignedNodes('', true, 'kat-breadcrumb-item')
  breadcrumbItems: KatBreadcrumbItem[] = [];

  @state()
  collapseBreadcrumbItems = false;

  /**
   * Observes mobile viewport width as state so that Breadcrumb only reacts when
   * this changes values, instead of on every resize event.
   */
  @state()
  isMobileView = isMobile();

  static get styles() {
    return [baseStyles, styles];
  }

  private handleWindowResize() {
    this.isMobileView = isMobile();
  }

  private toggleHidden(item: KatBreadcrumbItem, force?: boolean) {
    item.classList.toggle('hidden', force);
    item.inert = item.classList.contains('hidden');
  }

  /**
   * Used on mobile views.
   * Hides all items except Home, top-level category, and the current page under an `...`
   */
  hideBreadcrumbItems() {
    if (isMobile() && this.breadcrumbItems.length > 2) {
      // Don't hide the first or last (current) breadcrumb item
      for (let i = 1; i < this.breadcrumbItems.length - 1; i++) {
        const currentItem = this.breadcrumbItems[i];
        this.toggleHidden(
          currentItem,
          !currentItem.classList.contains('collapsed-indicator')
        );
      }
    }
  }

  showBreadcrumbItems() {
    const breadcrumbItems = this.querySelectorAll<KatBreadcrumbItem>(
      'kat-breadcrumb-item'
    );

    breadcrumbItems.forEach(item => {
      this.toggleHidden(item, item.classList.contains('collapsed-indicator'));
    });
  }

  renderCollapsedBreadcrumbItem() {
    const collapsedBreadcrumbItem = document.createElement(
      'kat-breadcrumb-item'
    ) as KatBreadcrumbItem;
    collapsedBreadcrumbItem.label = '...';
    collapsedBreadcrumbItem.href = 'javascript:;'; // Links are only tabbable with an href. This href does nothing.
    collapsedBreadcrumbItem.classList.add('collapsed-indicator');
    collapsedBreadcrumbItem.addEventListener(
      'click',
      () => (this.collapseBreadcrumbItems = false)
    );

    if (this.breadcrumbItems.length > 2) {
      const secondLastItem =
        this.breadcrumbItems[this.breadcrumbItems.length - 1];
      secondLastItem.parentNode.insertBefore(
        collapsedBreadcrumbItem,
        secondLastItem
      );
    }
  }

  updated(changedProperties: Map<string, any>) {
    if (changedProperties.has('isMobileView')) {
      if (isMobile()) {
        this.collapseBreadcrumbItems = true;
      } else {
        this.collapseBreadcrumbItems = false;
      }
    }

    if (changedProperties.has('collapseBreadcrumbItems')) {
      if (this.collapseBreadcrumbItems) {
        this.hideBreadcrumbItems();
      } else {
        this.showBreadcrumbItems();
      }
    }
  }

  firstUpdated() {
    super.firstUpdated();

    // BreadcrumbItems are slotted, so we need to modify them after the initial render
    // and need to use real elements instead of HTMLTemplate'd items.
    this.renderCollapsedBreadcrumbItem();
  }

  render() {
    return html`
      ${!this.homeHref
        ? nothing
        : html`<kat-breadcrumb-item
            href=${this.homeHref}
            icon-name="home"
          ></kat-breadcrumb-item>`}
      <slot></slot>
    `;
  }
}
